import { PrivateRoute } from '@codezo/auth';
import { AuthRoleEnum } from '@codezo/graphql';
import { useLayoutEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { CommentTargetRedirect } from './CommentTargetRedirect';
import { PortalGate } from '../../layouts/PortalGate';
import {
	rememberNotificationDestination,
	takeNotificationDestination,
} from '../../lib/notification-destination';

/**
 * Where a notification's `(type, id)` leads in the vetter app. Every bell item
 * and emailed link points at `/n/:type/:id` rather than a real route, so pages
 * can move without breaking mail already sent — this path must never move.
 *
 * Adding a notification entity type on the backend means adding a case here.
 * Unknown types land on the job list rather than a dead end.
 */
function destinationFor(type: string, id: string): string {
	switch (type.toUpperCase()) {
		case 'VETTER_JOB':
			return `/jobs/${encodeURIComponent(id)}`;
		default:
			return '/jobs';
	}
}

function NotificationRedirect() {
	const { type = '', id = '' } = useParams<{ type: string; id: string }>();
	// A comment carries its own id, not the job's — the backend says where it lives.
	if (type.toUpperCase() === 'QUOTE_PRODUCT_COMMENT')
		return <CommentTargetRedirect commentId={id} />;
	return <Navigate to={destinationFor(type, id)} replace />;
}

/**
 * Guards `/n/:type/:id` and keeps its destination across signing in: the link
 * usually arrives by email on a device with no session, and a failed refresh
 * sends the supplier to the public app's login, which would otherwise drop
 * them on the job list. Same arrangement as the organization app's.
 */
export function NotificationLinkRoute() {
	const { isAuthenticated, user } = useAuth();
	const location = useLocation();

	// A layout effect so it lands before PrivateRoute redirects.
	useLayoutEffect(() => {
		if (!isAuthenticated) {
			rememberNotificationDestination(`${location.pathname}${location.search}`);
			return;
		}
		// Reached signed in — the stash has done its job; don't let it hijack a
		// later sign-in in this tab.
		takeNotificationDestination();
	}, [isAuthenticated, location.pathname, location.search]);

	return (
		<PrivateRoute
			isAuthenticated={isAuthenticated}
			user={user}
			requiredRole={AuthRoleEnum.Vetter}
		>
			<PortalGate>
				<NotificationRedirect />
			</PortalGate>
		</PrivateRoute>
	);
}
